/**
 * LocalStorage Utility Functions
 * localStorage 값을 가져오고 저장하는 유틸리티 함수
 */

/**
 * 사용자 정보 엔티티
 */
export type UserInfo = {
    riderId?: string;
    suppId?: string;
    riderName?: string | null;
    prmCode?: string;
    requiresPasswordSetup?: boolean;
    [key: string]: any;
};

/**
 * localStorage에서 값을 가져옵니다.
 * @param key 저장된 키
 * @param defaultValue 키가 없을 때 반환할 기본값
 * @returns 저장된 값 또는 기본값
 */
export const getLocalStorageItem = <T = string>(key: string, defaultValue?: T): T | null => {
    try {
        const item = localStorage.getItem(key);
        if (item === null) {
            return defaultValue ?? null;
        }
        return item as T;
    } catch (error) {
        return defaultValue ?? null;
    }
};

/**
 * localStorage에 값을 저장합니다.
 * @param key 저장할 키
 * @param value 저장할 값
 */
export const setLocalStorageItem = (key: string, value: string): void => {
    localStorage.setItem(key, value);
};

/**
 * localStorage에서 JSON 값을 가져옵니다.
 * @param key 저장된 키
 * @param defaultValue 키가 없을 때 반환할 기본값
 * @returns 파싱된 JSON 객체 또는 기본값
 */
export const getLocalStorageJson = <T = any>(key: string, defaultValue?: T): T | null => {
    try {
        const item = localStorage.getItem(key);
        if (item === null) {
            return defaultValue ?? null;
        }
        return JSON.parse(item) as T;
    } catch (error) {
        return defaultValue ?? null;
    }
};

/**
 * localStorage에 JSON 값을 저장합니다.
 * @param key 저장할 키
 * @param value 저장할 객체
 */
export const setLocalStorageJson = (key: string, value: any): void => {
    localStorage.setItem(key, JSON.stringify(value));
};

/**
 * localStorage 또는 sessionStorage에서 userInfo를 가져옵니다.
 * 로그인 상태 유지 시 localStorage, 미유지 시 sessionStorage에 저장됩니다.
 */
export const getUserInfo = (): UserInfo | null => {
    const fromLocal = getLocalStorageJson<UserInfo>('userInfo');
    if (fromLocal) return fromLocal;
    try {
        const raw = sessionStorage.getItem('userInfo');
        return raw ? (JSON.parse(raw) as UserInfo) : null;
    } catch {
        return null;
    }
};

/**
 * localStorage에 userInfo를 저장합니다.
 * @param userInfo 저장할 userInfo 객체
 */
export const setUserInfo = (userInfo: UserInfo): void => {
    setLocalStorageJson('userInfo', userInfo);
};

/**
 * localStorage에서 특정 키의 값을 제거합니다.
 * @param key 제거할 키
 */
export const removeLocalStorageItem = (key: string): void => {
    localStorage.removeItem(key);
};

/**
 * localStorage의 모든 값을 제거합니다.
 */
export const clearLocalStorage = (): void => {
    localStorage.clear();
};

/**
 * localStorage에서 requiresPasswordSetup 값을 가져옵니다.
 * userInfo 객체에서 가져옵니다.
 * @returns requiresPasswordSetup 값 또는 false
 */
export const getRequiresPasswordSetup = (): boolean => {
    const userInfo = getUserInfo();
    return userInfo?.requiresPasswordSetup ?? false;
};

